import React, { Component } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Alert, Container, Row } from 'reactstrap';

import bannerLogo from '../images/Jook_Web_White.png';

import api from '../utils/Api';
import { authContext } from '../contexts/AuthContext';
import * as JKYModel from '../custom-types';
import ResendVerification from './ResendVerification';
import GoogleSignInButton from './GoogleSignInButton';
import GoogleUsernameStep from './GoogleUsernameStep';
import { GoogleSignInResult, PendingGoogleSignup } from '../utils/GoogleSignIn';
import TwoStepCodeStep from './TwoStepCodeStep';

import './SignupView.scss';
import { trackEvent } from '../utils/Analytics';

interface SignupViewProps {}

const SignupView = (props: SignupViewProps) => {
  const location = useLocation();
  const [username, setUsername] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [acceptedTerms, setAcceptedTerms] = React.useState(false);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  // Set once the account is made and is waiting on the link in its sign-up email.
  const [sentTo, setSentTo] = React.useState('');
  const context = React.useContext(authContext);
  // Someone new who picked their Google account, and now picks a username.
  const [googlePending, setGooglePending] = React.useState<PendingGoogleSignup | null>(null);
  // Their Google account belongs to an account with two-step sign-in on: the code comes next.
  const [mfaToken, setMfaToken] = React.useState<string | null>(null);

  // Signing in sets the auth status; Public then sends them on to where they were headed.
  const signedInWithGoogle = (user: JKYModel.User, token: string, event: 'login' | 'sign_up', method = 'google') => {
    trackEvent(event, { method });
    context.setAuthStatus({ id: user.id, email: user.email, token: token });
    context.updateCurrentUser(user);
  };

  const handleGoogleResult = (result: GoogleSignInResult) => {
    if (result.kind === 'pending') {
      setError('');
      setGooglePending(result.pending);
    } else if (result.kind === 'mfa') {
      setError('');
      setMfaToken(result.mfaToken);
    } else {
      // They already had an account; this signs them in to it.
      signedInWithGoogle(result.user, result.token, 'login');
    }
  };

  const isValid = () => {
    let valid = false;

    if (username.length > 0 && email.length > 0 && password.length > 0 && confirmPassword.length > 0) {
      valid = true;
    }

    if (username.length === 0) {
      showError('You must enter a username');
      valid = false;
    } else if (email.length === 0) {
      showError('You must enter an email address');
      valid = false;
    } else if (!isEmail(email)) {
      showError('You must enter a valid email address');
      valid = false;
    } else if (password.length === 0) {
      showError('You must enter a password');
      valid = false;
    } else if (password.length < 8) {
      showError('Your password must be at least 8 characters!');
      valid = false;
    } else if (confirmPassword.length === 0) {
      showError('You must retype your password');
      valid = false;
    } else if (password !== confirmPassword) {
      showError('Your passwords do not match.\nPlease check and try again.');
      valid = false;
    } else if (!acceptedTerms) {
      showError('Please agree to the Terms of Service and Privacy Policy.');
      valid = false;
    }

    return valid;
  };

  const isEmail = (email: string) => {
    const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!isValid()) {
      return;
    }

    setLoading(true);
    api
      .auth('signup', 'POST', {
        email: email,
        username: username,
        password: password,
        acceptedTerms: acceptedTerms,
      })
      .then(response => {
        if (response.verificationRequired) {
          trackEvent('sign_up', { method: 'email' });
          setSentTo(response.email || email);
          setLoading(false);
          return;
        }
        let user: JKYModel.User;
        let token: string;
        if (response.user) {
          user = response.user;
        }
        if (response.token) {
          token = response.token;
        }
        if (user && token) {
          trackEvent('sign_up', { method: 'email' });
          context.setAuthStatus({ id: user.id, email: user.email, token: token });
          context.updateCurrentUser(user);
        }

        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
        showError(error.message);
        console.log('There has been a problem with your signup operation: ' + error.message);
      });

    // Accounts.createUser({ username, email, password }, err => {
    //   if (err) {
    //     this.setState({ error: err.reason });
    //   } else {
    //     this.setState({ error: '' });
    //   }
    // });
  };

  const showError = (errorText: string) => {
    setError(errorText);
  };

  return (
    <Container fluid>
      <Row className="public-header-row">
        <div className="col public-header">
          <div className="d-flex justify-content-between">
            <Link to="/">
              <img className="logo" src={bannerLogo} alt="Jookable.com logo image" />
            </Link>
            <div />
          </div>
        </div>
      </Row>

      <Row className="g-0">
        <div className="col">
          <div className="public-page-content">
            {sentTo ? (
              <div className="public-page-formwrapper">
                <h1 className="public-page-formaccess">Check Your Email</h1>
                <div className="public-page-form">
                  <Alert color="success">
                    We sent a link to {sentTo}. Click it to confirm your email, then sign in.
                  </Alert>
                  <ResendVerification email={sentTo} />
                </div>
                <div className="signin-link-wrapper">
                  <Link to="/" state={location.state} className="signin-link">
                    Back to Sign In
                  </Link>
                </div>
              </div>
            ) : mfaToken ? (
              <TwoStepCodeStep
                mfaToken={mfaToken}
                onSignedIn={(user, token) => signedInWithGoogle(user, token, 'login', 'google_two_step')}
                onCancel={() => setMfaToken(null)}
              />
            ) : googlePending ? (
              <GoogleUsernameStep
                pending={googlePending}
                onSignedIn={(user, token) => signedInWithGoogle(user, token, 'sign_up')}
                onCancel={() => setGooglePending(null)}
              />
            ) : (
              <div className="public-page-formwrapper">
                <h1 className="public-page-formaccess">Create New Account</h1>
                <GoogleSignInButton mode="signup" onResult={handleGoogleResult} onError={showError} />
                <form onSubmit={handleSubmit} noValidate className="public-page-form">
                  {error && <Alert color="danger">{error}</Alert>}
                  <input
                    type="text"
                    name="username"
                    placeholder="Username"
                    value={username}
                    onChange={e => setUsername(e.target.value.trim())}
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={email}
                    onChange={e => setEmail(e.target.value.trim())}
                  />
                  <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={password}
                    onChange={e => setPassword(e.target.value.trim())}
                  />
                  <input
                    type="password"
                    name="confirmPassword"
                    placeholder="Retype Password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value.trim())}
                  />
                  <label className="signup-terms">
                    <input
                      type="checkbox"
                      name="acceptedTerms"
                      checked={acceptedTerms}
                      onChange={e => setAcceptedTerms(e.target.checked)}
                    />
                    <span>
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                      </Link>
                      .
                    </span>
                  </label>
                  <button className="button">Create New Account</button>
                </form>

                <div className="signin-link-wrapper">
                  <Link to="/" state={location.state} className="signin-link">
                    Already Have an Account?
                  </Link>
                  <br />
                  <br />
                  <Link to="/venues" className="signin-link">
                    Venues near you
                  </Link>
                </div>

                <div className="store-promo">
                  <h3 style={{ paddingTop: 15, textAlign: 'center' }}>
                    {' '}
                    {'Host your own jukebox on any TV, then add videos from any phone or computer.'}{' '}
                  </h3>
                </div>
              </div>
            )}
          </div>
        </div>
      </Row>
    </Container>
  );
};

export default SignupView;
